/**
 * Diagram SVG helpers
 *
 * Produces and reads the Diagram_File: a rendered SVG with the Excalidraw scene
 * embedded, the Diagram_Title in a `<title>` element, and the Diagram_Id in a
 * `data-map-diagram-id` attribute on the root `<svg>` (plus
 * `data-map-diagram-schema="1"`). See Requirements 2.2 and 2.3.
 *
 * `exportDiagramSvg` imports `@excalidraw/excalidraw` dynamically, so importing
 * this module does not pull the heavy Excalidraw bundle into the main chunk;
 * the package loads only when an export actually runs (and `exportToSvg` needs
 * a real browser, not jsdom). `readDiagramSvgMeta` and `svgTextToBlob` are pure
 * DOM-string helpers (DOMParser / XMLSerializer) with no Excalidraw dependency,
 * so they can be unit-tested against fixture SVG strings.
 */

// Must run before Excalidraw loads: points its font loader at our own origin.
import "@/components/diagrams/excalidrawAssetPath"
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types"
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types"

/** The current Diagram_File schema version, written as `data-map-diagram-schema`. */
export const DIAGRAM_SCHEMA_VERSION: string = "1"

/** The attribute on the root `<svg>` that carries the Diagram_Id. */
export const DIAGRAM_ID_ATTRIBUTE: string = "data-map-diagram-id"

/** The attribute on the root `<svg>` that carries the schema version. */
export const DIAGRAM_SCHEMA_ATTRIBUTE: string = "data-map-diagram-schema"

/**
 * The MIME type used for Diagram_File blobs and for `DOMParser`. Typed as the
 * `DOMParser` literal so it can be passed to `parseFromString` directly.
 */
const SVG_MIME_TYPE: DOMParserSupportedType = "image/svg+xml"

/**
 * The parts of an Excalidraw scene needed to export a Diagram_File. A subset of
 * what `loadFromBlob` returns, so a loaded scene can be passed straight in.
 */
export interface DiagramScene {
  /** The scene's elements (including deleted ones; Excalidraw filters them). */
  elements: readonly ExcalidrawElement[]
  /** The scene's app state (canvas settings, current-item defaults, etc.). */
  appState: Partial<AppState>
  /** The scene's binary files (embedded images), keyed by file id. */
  files: BinaryFiles
}

/**
 * The metadata read back from a Diagram_File's root `<svg>`.
 */
export interface DiagramSvgMeta {
  /** The Diagram_Id from the `data-map-diagram-id` attribute. */
  diagramId: string
  /** The Diagram_Title from the first-child `<title>` element, or "" if none. */
  title: string
  /** The schema version from `data-map-diagram-schema`, or null if absent. */
  schema: string | null
}

/**
 * Export an Excalidraw scene to a Diagram_File SVG string.
 *
 * Calls Excalidraw's `exportToSvg` with the scene's own `appState` merged with
 * the fixed export flags required for GitLab rendering (embedded scene, white
 * background, dark mode off), then post-processes the resulting `<svg>`:
 * inserts `<title>` as the first child, and sets the `data-map-diagram-id` and
 * `data-map-diagram-schema` attributes on the root element. The result is
 * serialised with `XMLSerializer`.
 *
 * `@excalidraw/excalidraw` is imported dynamically so this function only loads
 * the package when first called. It requires a real browser DOM (not jsdom).
 *
 * @param params - The export inputs.
 * @param params.scene - The Excalidraw scene to export (elements, appState, files).
 * @param params.id - The Diagram_Id to embed in the root `<svg>`.
 * @param params.title - The Diagram_Title to embed as the `<title>` element.
 * @returns The serialised Diagram_File SVG text.
 */
export async function exportDiagramSvg({
  scene,
  id,
  title,
}: {
  scene: DiagramScene
  id: string
  title: string
}): Promise<string> {
  // Dynamic import keeps Excalidraw out of the main bundle; it loads only here.
  const { exportToSvg } = await import("@excalidraw/excalidraw")

  const svgElement: SVGSVGElement = await exportToSvg({
    elements: scene.elements,
    files: scene.files,
    appState: {
      ...scene.appState,
      // Fixed export flags (Requirement 2.2): embed the scene so the file is
      // re-editable, render a white background, and never export dark mode, so
      // the file renders the same in GitLab whatever theme the editor used.
      exportEmbedScene: true,
      exportBackground: true,
      exportWithDarkMode: false,
      viewBackgroundColor: "#ffffff",
    },
    exportPadding: 20,
  })

  // Post-process: a <title> as the first child, and the two data- attributes on
  // the root <svg> (Requirement 2.3). `ownerDocument` gives us a document to
  // create the <title> element in without touching the live page DOM.
  const ownerDocument: Document = svgElement.ownerDocument
  const titleElement: SVGTitleElement = ownerDocument.createElementNS(
    "http://www.w3.org/2000/svg",
    "title",
  )
  titleElement.textContent = title
  svgElement.insertBefore(titleElement, svgElement.firstChild)

  svgElement.setAttribute(DIAGRAM_ID_ATTRIBUTE, id)
  svgElement.setAttribute(DIAGRAM_SCHEMA_ATTRIBUTE, DIAGRAM_SCHEMA_VERSION)

  return new XMLSerializer().serializeToString(svgElement)
}

/**
 * Read the Diagram_Id, Diagram_Title, and schema version from a Diagram_File
 * SVG string.
 *
 * Pure: parses with `DOMParser` and reads the root `<svg>`, so it can be tested
 * on fixture strings. Fails loudly: a string that does not parse as an SVG, or
 * that carries no `data-map-diagram-id`, throws rather than returning a
 * partial result, because callers rely on the id to address the diagram.
 *
 * @param svgText - The Diagram_File SVG text to read.
 * @returns The diagram's id, title, and schema version.
 * @throws Error if the text is not an SVG or has no `data-map-diagram-id`.
 */
export function readDiagramSvgMeta(svgText: string): DiagramSvgMeta {
  const parsed: Document = new DOMParser().parseFromString(svgText, SVG_MIME_TYPE)

  // DOMParser does not throw on malformed input; it returns a document with a
  // <parsererror> element, or a root that is not <svg>. Reject both loudly.
  const root: Element | null = parsed.documentElement
  if (root === null || root.localName !== "svg") {
    throw new Error("Diagram file is not a valid SVG")
  }
  if (parsed.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Diagram file is not well-formed SVG")
  }

  const diagramId: string | null = root.getAttribute(DIAGRAM_ID_ATTRIBUTE)
  if (diagramId === null || diagramId === "") {
    throw new Error(
      `Diagram file is missing its ${DIAGRAM_ID_ATTRIBUTE} attribute`,
    )
  }

  // The title is the first-child <title> element; read it directly rather than
  // searching, to match how the file is written. An absent title is allowed
  // here (the id is what identifies the diagram); callers can treat "" as none.
  const firstTitle: Element | null = root.getElementsByTagName("title").item(0)
  const title: string = firstTitle?.textContent ?? ""

  const schema: string | null = root.getAttribute(DIAGRAM_SCHEMA_ATTRIBUTE)

  return { diagramId, title, schema }
}

/**
 * Wrap a Diagram_File SVG string in a Blob, for passing to Excalidraw's
 * `loadFromBlob` when opening a diagram for editing.
 *
 * @param svgText - The Diagram_File SVG text.
 * @returns A Blob of type `image/svg+xml` containing the SVG text.
 */
export function svgTextToBlob(svgText: string): Blob {
  return new Blob([svgText], { type: SVG_MIME_TYPE })
}
